<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <!-- Per Page -->
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ path: '/settings' }"
            >
              <i class="fa-solid fa-arrow-left"></i>
              <span class="ml-25">Back</span>
            </b-button>

            <h3 class="ml-1">Priorities</h3>
          </b-col>

          <b-col cols="12" md="5">
            <div class="d-flex align-items-center justify-content-end">
              <div class="d-flex align-items-center mr-1">
                <label>Show</label>
                <v-select
                  v-model="perPage"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label>entries </label>
              </div>

              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                cle
                placeholder="Search..."
              />
              <b-button
                v-if="$Can('add-priority')"
                variant="primary"
                @click="addPriority()"
              >
                <span class="text-nowrap">Add Priority</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <b-table
          sticky-header
          striped
          hover
          bordered
          ref="refPriorityListTable"
          :outlined="true"
          :fields="fields"
          :items="filteredPriorities"
          responsive="sm"
          :small="true"
          class="table table-sm table-responsive"
          :filter="filter"
          @filtered="onFiltered"
          :filter-included-fields="filterOn"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
          @row-clicked="$Can('edit-priority') && editPriority($event.id)"
        >
          <template #cell(objective)="data">
            <div style="font-size: 1rem;font-weight: bold;" class="p-25">
              <p>
                {{
                  data.item.objective.title ? data.item.objective.title : "-"
                }}
              </p>
            </div>
          </template>

          <template #cell(year)="data">
            <div style="font-size: 1rem;" class="p-25">
              <p>{{ data.value }}</p>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge
              v-if="data.item.status == 'Active'"
              pill
              :variant="`light-success`"
              class="text-capitalize p-25"
            >
              {{ data.item.status }}
            </b-badge>

            <b-badge
              v-else
              pill
              :variant="`light-danger`"
              class="text-capitalize p-25"
            >
              {{ data.item.status }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div>
              <b-button
                v-if="$Can('edit-priority')"
                variant="primary"
                class="mr-25 p-50"
                @click="editPriority(data.item.id)"
              >
                <i class="fa-solid fa-pen-to-square mr-25"></i>
                <span> Edit</span>
              </b-button>

              <b-button
                v-if="$Can('delete-priority')"
                variant="primary"
                @click="deletePriority(data.item.id)"
                class="p-50"
              >
                <span>{{
                  data.item.status === "Active" ? "Deactivate" : "Activate"
                }}</span>
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Add pagination below table -->
        <div class="mx-2 mb-1 mt-1">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                {{ dataMeta.of }} entries</span
              >
            </b-col>

            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormRating,
} from "bootstrap-vue";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import vSelect from "vue-select";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormRating,
    vSelect,
    ToastificationContent,
  },

  setup() {
    const PRIORITY_APP_STORE_MODULE_NAME = "settings";
    // Register module
    if (!store.hasModule(PRIORITY_APP_STORE_MODULE_NAME))
      store.registerModule(PRIORITY_APP_STORE_MODULE_NAME, settingsStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(PRIORITY_APP_STORE_MODULE_NAME))
        store.unregisterModule(PRIORITY_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      priorities: [],
      filter: null,
      filterOn: ["objective", "title"],
      loading: false,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 50, 100],
      perPage: localStorage.getItem("priorityShow") ?? 50,
      currentPage: 1,
      totalRows: 0,

      perPageOptions: [10, 25, 50, 100],
      fields: [
        {
          key: "objective",
          sortable: true,
          class: "text-left",
          thStyle: "width:60%;color:white!important",
          sortByFormatted: true,
          formatter: (value) => value?.title || "-",
        },
        {
          key: "year",
          sortable: true,
          class: "text-left",
          thStyle: "width:10%;color:white!important",
        },
        {
          key: "status",
          sortable: false,
          class: "text-center",
          thStyle: "width:10%;color:white!important",
        },
        {
          key: "actions",
          sortable: false,
          class: "text-center",
          thStyle: "width:20%;color:white!important",
        },
      ],
    };
  },

  methods: {
    getPriorities() {
      this.loading = true;
      store
        .dispatch("settings/getAllPriorities")
        .then((res) => {
          this.priorities = res.data;
          this.totalRows = res.data.length;
          this.loading = false;
        })
        .catch((error) => {
          this.$router.push({ name: "settings" });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "An error occurred",
              text: "Please try again later or contact support.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    editPriority(id) {
      this.$router.push({
        name: "settings-priority-edit",
        params: { id: id },
      });
    },

    deletePriority(id) {
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want to change status of this Priority?",
          {
            size: "sm",
            title: "Confirm Deletion",
            okVariant: "primary",
            okTitle: "Yes",
            cancelTitle: "No",
            cancelVariant: "outline-secondary",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.loading = true;
            const priority = this.priorities.find((met) => met.id === id);

            if (priority) {
              store
                .dispatch("settings/deletePriority", priority)
                .then((response) => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Successfully",
                      text: "✔️ Priority status changed successfully",
                      icon: "ThumbsUpIcon",
                      variant: "success",
                    },
                  });

                  this.getPriorities();
                })
                .catch((error) => {
                  this.formShow = false;
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "An error occurred",
                      text: "Please try again later or contact support.",
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  });
                });
            }
          }
        });
    },

    addPriority() {
      this.$router.push({ name: "settings-priority-add" });
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },

  created() {
    this.getPriorities();
  },

  computed: {
    dataMeta() {
      return {
        from:
          this.totalRows != 0 ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to:
          this.perPage * (this.currentPage - 1) + Number(this.perPage) <
          this.totalRows
            ? this.perPage * (this.currentPage - 1) + Number(this.perPage)
            : this.totalRows,
        of: this.totalRows,
      };
    },
    filteredPriorities() {
      let filtered = this.priorities;

      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        filtered = this.priorities.filter(
          (item) =>
            (item.objective.title &&
              item.objective.title.toLowerCase().includes(searchTerm)) ||
            item.year.toString().includes(searchTerm)
        );
      }

      return filtered;
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.table-responsive.b-table-sticky-header {
  max-height: 75vh !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
